import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  BadgeCheck,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  Clock,
  XCircle,
} from "lucide-react";
import {
  decideDiscountApproval,
  listCommercialApprovals,
  type CommercialApprovalRow,
} from "./lib/api";
import { invalidateCrmData } from "./lib/crmSync";
import { useCrmIdentity } from "./lib/crmIdentity";

const STATUS_FILTERS = [
  "PENDING_APPROVAL",
  "ALL",
  "APPROVED",
  "REJECTED",
  "CHANGES_REQUESTED",
  "EXPIRED",
  "CONSUMED",
] as const;

const STATUS_CHIP_LABEL: Record<string, string> = {
  DRAFT: "DRAFT",
  PENDING_APPROVAL: "PENDING APPROVAL",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  CHANGES_REQUESTED: "CHANGES REQUESTED",
  EXPIRED: "EXPIRED",
  REVOKED: "REVOKED",
  CONSUMED: "CONSUMED",
};

const DECISION_ERROR_COPY: Record<string, string> = {
  self_approval_prohibited: "You cannot approve your own request.",
  "self approval prohibited": "You cannot approve your own request.",
  request_failed_403:
    "Decision rejected — you may lack this tier's authority or the requester is you.",
  approval_authority_insufficient:
    "You do not hold the approval authority this tier requires.",
  approval_expired:
    "This request already expired. It must be re-requested.",
  approval_state_not_decidable:
    "This request was already decided or is no longer decidable.",
  approval_not_found: "This request could not be found.",
};

function formatCents(cents: number): string {
  const value = cents / 100;
  return `${value < 0 ? "-" : ""}$${Math.abs(value)
    .toFixed(2)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
}

function ageLabel(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "unknown age";
  const minutes = Math.max(0, Math.floor((Date.now() - then) / 60000));
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

export function CommercialApprovals() {
  const identity = useCrmIdentity();
  const can = useCallback(
    (capability: string) =>
      (identity.capabilities as readonly string[]).includes(capability),
    [identity.capabilities],
  );
  const isApprover =
    can("commercial.discount.approve.tier1") ||
    can("commercial.discount.approve.tier2") ||
    can("commercial.discount.approve.executive");

  const [statusFilter, setStatusFilter] = useState<string>("PENDING_APPROVAL");
  const [onlyMine, setOnlyMine] = useState(false);
  const [highDiscountOnly, setHighDiscountOnly] = useState(false);
  const [repQuery, setRepQuery] = useState("");
  const [rows, setRows] = useState<CommercialApprovalRow[]>([]);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [overrideChecked, setOverrideChecked] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const [decisionError, setDecisionError] = useState<{
    id: string;
    code: string;
  } | null>(null);

  const refresh = useCallback(() => {
    listCommercialApprovals(statusFilter, onlyMine, 50)
      .then((value) => {
        setRows(value);
        setLoadState("ready");
      })
      .catch(() => setLoadState("error"));
  }, [onlyMine, statusFilter]);

  useEffect(() => {
    setLoadState("loading");
    refresh();
  }, [refresh]);

  const visible = useMemo(
    () =>
      rows.filter(
        (row) =>
          (!highDiscountOnly || row.discountBps >= 1500) &&
          (repQuery.trim().length === 0 ||
            `${row.requestedByName} ${row.requesterEmail ?? ""}`
              .toLowerCase()
              .includes(repQuery.trim().toLowerCase())),
      ),
    [highDiscountOnly, repQuery, rows],
  );

  async function decide(
    row: CommercialApprovalRow,
    decision: "APPROVED" | "REJECTED" | "CHANGES_REQUESTED",
  ) {
    if (submittingId) return;
    const trimmedNote = note.trim();
    if (
      (decision === "REJECTED" || decision === "CHANGES_REQUESTED") &&
      trimmedNote.length < 4
    ) {
      setDecisionError({ id: row.id, code: "note_too_short" });
      return;
    }
    const wantsOverride =
      overrideChecked &&
      can("commercial.discount.approve.executive") &&
      row.requestedByUserId === identity.userId;
    if (wantsOverride && overrideReason.trim().length < 16) {
      setDecisionError({ id: row.id, code: "override_reason_too_short" });
      return;
    }
    setSubmittingId(row.id);
    setDecisionError(null);
    try {
      await decideDiscountApproval({
        requestId: row.id,
        decision,
        note: trimmedNote || null,
        executiveOverride: wantsOverride,
        overrideReason: wantsOverride ? overrideReason.trim() : null,
      });
      invalidateCrmData();
      refresh();
      setExpandedId(null);
      setNote("");
      setOverrideChecked(false);
      setOverrideReason("");
    } catch (error) {
      setDecisionError({
        id: row.id,
        code: error instanceof Error ? error.message : "decision_rejected",
      });
    } finally {
      setSubmittingId(null);
    }
  }

  return (
    <div className="page approvals-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">COMMERCIAL GOVERNANCE</span>
          <h1>Approvals</h1>
          <p>
            Discount approvals are decided by authority level. Every decision is
            recorded; self-approval is prohibited outside a recorded executive
            override.
          </p>
        </div>
        <Link className="secondary-btn policy-link" to="/sales-knowledge">
          <BookOpen size={15} /> View Commercial Policy
        </Link>
      </div>

      <section className="panel">
        <div className="queue-filters">
          <label>
            Status
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              {STATUS_FILTERS.map((value) => (
                <option key={value} value={value}>
                  {STATUS_CHIP_LABEL[value] ?? value}
                </option>
              ))}
            </select>
          </label>
          {isApprover && (
            <label className="filter-toggle">
              <input
                type="checkbox"
                checked={onlyMine}
                onChange={(event) => setOnlyMine(event.target.checked)}
              />
              Only mine
            </label>
          )}
          <label className="filter-toggle">
            <input
              type="checkbox"
              checked={highDiscountOnly}
              onChange={(event) => setHighDiscountOnly(event.target.checked)}
            />
            High discount (≥15%)
          </label>
          <label className="filter-rep">
            Rep
            <input
              value={repQuery}
              onChange={(event) => setRepQuery(event.target.value)}
              placeholder="Filter by rep name"
            />
          </label>
        </div>

        {loadState === "loading" && <p>Loading approval queue…</p>}
        {loadState === "error" && (
          <p role="alert">
            The approval queue could not be loaded from the server. No decisions
            can be made until it loads.
          </p>
        )}
        {loadState === "ready" && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Rep</th>
                  <th>Plan · Locations</th>
                  <th>Program</th>
                  <th>Requested</th>
                  <th>Status</th>
                  <th>Age</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => (
                  <ApprovalRowBlock
                    key={row.id}
                    row={row}
                    expanded={expandedId === row.id}
                    onToggle={() => {
                      setExpandedId(expandedId === row.id ? null : row.id);
                      setDecisionError(null);
                      setNote("");
                      setOverrideChecked(false);
                      setOverrideReason("");
                    }}
                    can={can}
                    identity={identity}
                    note={note}
                    setNote={setNote}
                    overrideChecked={overrideChecked}
                    setOverrideChecked={setOverrideChecked}
                    overrideReason={overrideReason}
                    setOverrideReason={setOverrideReason}
                    submitting={submittingId === row.id}
                    error={
                      decisionError && decisionError.id === row.id
                        ? decisionError.code
                        : null
                    }
                    onDecide={(decision) => void decide(row, decision)}
                  />
                ))}
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={7}>No approval requests match this view.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

type ApprovalRowBlockProps = {
  row: CommercialApprovalRow;
  expanded: boolean;
  onToggle: () => void;
  can: (capability: string) => boolean;
  identity: ReturnType<typeof useCrmIdentity>;
  note: string;
  setNote: (value: string) => void;
  overrideChecked: boolean;
  setOverrideChecked: (value: boolean) => void;
  overrideReason: string;
  setOverrideReason: (value: string) => void;
  submitting: boolean;
  error: string | null;
  onDecide: (decision: "APPROVED" | "REJECTED" | "CHANGES_REQUESTED") => void;
};

function ApprovalRowBlock({
  row,
  expanded,
  onToggle,
  can,
  identity,
  note,
  setNote,
  overrideChecked,
  setOverrideChecked,
  overrideReason,
  setOverrideReason,
  submitting,
  error,
  onDecide,
}: ApprovalRowBlockProps) {
  const selfPath =
    can("commercial.discount.approve.executive") &&
    row.requestedByUserId === identity.userId;
  const pending = row.status === "PENDING_APPROVAL";

  return (
    <>
      <tr className={`approval-row${expanded ? " expanded" : ""}`}>
        <td>
          <strong>{row.organizationName}</strong>
          <small>{row.program !== "NONE" ? row.program.replaceAll("_", " ") : ""}</small>
        </td>
        <td>{row.requestedByName || "Unknown rep"}</td>
        <td>
          {row.plan.charAt(0).toUpperCase() + row.plan.slice(1)} ·{" "}
          {row.locationCount} loc
        </td>
        <td>
          {(row.discountBps / 100).toFixed(1)}%
          {row.durationMonths != null ? ` · ${row.durationMonths}mo` : ""}
        </td>
        <td>
          {formatCents(row.listTotalCents)} → {formatCents(row.netCents)}
          <small>{new Date(row.createdAt).toLocaleString()}</small>
        </td>
        <td>
          <span className={`status-chip status-${row.status.toLowerCase()}`}>
            {STATUS_CHIP_LABEL[row.status] ?? row.status}
          </span>
          {row.requiredTier !== "NONE" && pending && (
            <small className="tier-caption">{row.requiredTier}</small>
          )}
        </td>
        <td>
          <button type="button" className="expand-toggle" onClick={onToggle}>
            {expanded ? "Hide" : "Review"} <ChevronDown size={13} />
          </button>
          <small>{ageLabel(row.createdAt)}</small>
        </td>
      </tr>
      {expanded && (
        <tr className="economics-detail-row">
          <td colSpan={7}>
            <div className="economics-detail">
              <div className="econ-block">
                <h4>STANDARD</h4>
                <dl>
                  <div>
                    <dt>Standard price</dt>
                    <dd>{formatCents(row.listTotalCents)}/mo</dd>
                  </div>
                  <div>
                    <dt>Effective base commission rate</dt>
                    <dd>{(row.baseCommissionBps / 100).toFixed(2)}%</dd>
                  </div>
                  <div>
                    <dt>Customer price without discount</dt>
                    <dd>{formatCents(row.listTotalCents)}/mo</dd>
                  </div>
                </dl>
              </div>
              <div className="econ-block proposed">
                <h4>PROPOSED</h4>
                <dl>
                  <div>
                    <dt>Discount</dt>
                    <dd>
                      -{formatCents(row.discountCents)} (
                      {(row.discountBps / 100).toFixed(1)}%)
                    </dd>
                  </div>
                  <div>
                    <dt>Customer price</dt>
                    <dd>{formatCents(row.netCents)}/mo</dd>
                  </div>
                  <div>
                    <dt>Adjusted commission</dt>
                    <dd>
                      {formatCents(row.adjustedCommissionCents)} (
                      {(row.effectiveRateBps / 100).toFixed(2)}%)
                    </dd>
                  </div>
                  <div>
                    <dt>Company retained</dt>
                    <dd>{formatCents(row.companyRetainedCents)}/mo</dd>
                  </div>
                </dl>
              </div>
              <div className="reason-block">
                <h4>REASON</h4>
                <p>{row.reason}</p>
                {row.supportingNote && <p className="supporting">{row.supportingNote}</p>}
                <p className="meta">
                  Program {row.program.replaceAll("_", " ")} · Required tier{" "}
                  {row.requiredTier} · Expires{" "}
                  {new Date(row.expiresAt).toLocaleString()}
                  {row.decidedAt
                    ? ` · Decided ${new Date(row.decidedAt).toLocaleString()}`
                    : ""}
                </p>
              </div>
            </div>
            {pending && (
              <div className="decision-box">
                <label>
                  Decision note
                  <input
                    value={note}
                    maxLength={2000}
                    onChange={(event) => setNote(event.target.value)}
                    placeholder="Required for reject or request changes (min 4 characters)"
                  />
                </label>
                {selfPath && (
                  <label className="override-control">
                    <input
                      type="checkbox"
                      checked={overrideChecked}
                      onChange={(event) =>
                        setOverrideChecked(event.target.checked)
                      }
                    />
                    Executive override of my own request — permanently recorded
                    and visible to audit with my written reason.
                  </label>
                )}
                {selfPath && overrideChecked && (
                  <label>
                    Override reason (minimum 16 characters, permanent record)
                    <textarea
                      value={overrideReason}
                      maxLength={2000}
                      rows={2}
                      onChange={(event) => setOverrideReason(event.target.value)}
                    />
                  </label>
                )}
                {error && (
                  <p role="alert" className="decision-error">
                    <AlertTriangle size={14} />{" "}
                    {error === "note_too_short"
                      ? "A note of at least 4 characters is required to reject or request changes."
                      : error === "override_reason_too_short"
                        ? "An executive override needs a written reason of at least 16 characters."
                        : DECISION_ERROR_COPY[error] ??
                          `The decision was rejected (${error.replaceAll("_", " ")}).`}
                  </p>
                )}
                <div className="decision-actions">
                  <button
                    type="button"
                    className="primary-btn"
                    disabled={submitting}
                    onClick={() => onDecide("APPROVED")}
                  >
                    <CheckCircle2 size={14} /> Approve
                  </button>
                  <button
                    type="button"
                    className="secondary-btn reject"
                    disabled={submitting}
                    onClick={() => onDecide("CHANGES_REQUESTED")}
                  >
                    <Clock size={14} /> Request changes
                  </button>
                  <button
                    type="button"
                    className="secondary-btn reject"
                    disabled={submitting}
                    onClick={() => onDecide("REJECTED")}
                  >
                    <XCircle size={14} /> Reject
                  </button>
                </div>
              </div>
            )}
            {!pending && row.decisionNote && (
              <p className="decision-note-recorded">
                <BadgeCheck size={13} /> Decision note: {row.decisionNote}
              </p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
